/**
 * The panel's time zone, and wall-clock helpers for the scheduling inputs.
 *
 * One zone drives everything: it goes out as `X-Timezone` on every request,
 * the API reads wall clocks (`*_local`) and `from`/`to` dates in it, and every
 * timestamp on screen is formatted in it. Keeping those three the same is the
 * whole point — a picker that shows Berlin while the header says Kolkata
 * schedules the send five and a half hours off.
 *
 * The zone is always this browser's, picked up from wherever the admin signs
 * in — there is no setting for it. It is read on every call rather than
 * captured at load: a laptop can change zone while the tab is open, and the
 * next request should follow it.
 *
 * Wall-clock strings are handled as strings and never go through `new
 * Date(value)`, which would hide the zone this module is careful to name.
 */

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** The zone the panel works in: this browser's. */
export function panelTimeZone(): string {
  return browserTimeZone();
}

const PARTS = new Map<string, Intl.DateTimeFormat>();

function partsFormat(zone: string): Intl.DateTimeFormat {
  let format = PARTS.get(zone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    PARTS.set(zone, format);
  }
  return format;
}

function wallClock(date: Date, zone: string) {
  const parts = partsFormat(zone).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "00";
  return {
    local: `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`,
    seconds: get("second"),
  };
}

/** Now, as a `YYYY-MM-DDTHH:mm` in the panel's zone — the shape the picker emits. */
export function nowLocal(zone = panelTimeZone()): string {
  return wallClock(new Date(), zone).local;
}

/** Today, as a `YYYY-MM-DD` in the panel's zone. */
export function todayLocal(zone = panelTimeZone()): string {
  return nowLocal(zone).slice(0, 10);
}

/**
 * A stored UTC instant as the wall clock the picker shows, so an edit form is
 * prefilled with what the API saved. Empty for null or garbage.
 *
 * From `*_at` rather than `*_local.slice(0, 16)`: the `_local` copy is in the
 * zone of the request that fetched it, and this is in the zone the save will
 * be sent with. They only differ if the preference changed in between, but
 * then this is the one that round-trips.
 */
export function isoToLocal(iso: string | null, zone = panelTimeZone()): string {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : wallClock(date, zone).local;
}

/**
 * A `YYYY-MM-DDTHH:mm` wall clock in `zone`, as a real UTC instant.
 *
 * For the few endpoints that still take instants (the activity log). Guesses
 * the offset, then corrects it once, which lands on the right side of a
 * daylight-saving jump for any time that exists.
 */
export function localToIso(local: string, zone = panelTimeZone()): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(local);
  if (!match) return undefined;
  const [, y, mo, d, h, mi, s = "00"] = match;
  const asUtc = Date.UTC(+y!, +mo! - 1, +d!, +h!, +mi!, +s);
  if (Number.isNaN(asUtc)) return undefined;

  const offsetAt = (instant: number) => {
    const { local: seen, seconds } = wallClock(new Date(instant), zone);
    const [date, time] = seen.split("T");
    const [sy, smo, sd] = date!.split("-").map(Number);
    const [sh, smi] = time!.split(":").map(Number);
    return Date.UTC(sy!, smo! - 1, sd!, sh!, smi!, Number(seconds)) - instant;
  };

  let instant = asUtc - offsetAt(asUtc);
  instant = asUtc - offsetAt(instant);
  return new Date(instant).toISOString();
}

/**
 * The zone with its current offset, for labels: `Europe/Berlin (GMT+2)`.
 *
 * Shown beside every picker, because a `datetime-local` control shows no zone
 * at all and an operator will otherwise assume whichever one they are in.
 */
export function timeZoneLabel(zone = panelTimeZone()): string {
  try {
    const offset = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" })
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value;
    return offset ? `${zone} (${offset})` : zone;
  } catch {
    return zone;
  }
}

export type ScheduleMode = "now" | "later";

/** True when the schedule half of a form is complete and not in the past. */
export function scheduleIsValid(mode: ScheduleMode, local: string): boolean {
  return mode === "now" || (local !== "" && local >= nowLocal());
}
